import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Play, CheckCircle } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { topicsApi, Topic } from '../api/topics';
import { useProgressStore } from '../state/progressStore';

export const TrainingFull: React.FC = () => {
  const [topics, setTopics] = useState<Topic[]>([]);
  const [loading, setLoading] = useState(true);
  const { getProgress } = useProgressStore();

  useEffect(() => {
    const fetchTopics = async () => {
      try {
        const data = await topicsApi.getAll();
        setTopics(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error fetching topics:', error);
        setTopics([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTopics();
  }, []);

  const totalFlashcards = Array.isArray(topics) ? topics.reduce((sum, topic) => sum + (topic.flashcards?.length || 0), 0) : 0;
  const totalQuestions = Array.isArray(topics) ? topics.reduce((sum, topic) => sum + (topic.questions?.length || 0), 0) : 0;
  const totalSections = Array.isArray(topics) ? topics.reduce((sum, topic) => sum + (topic.sections?.length || 0), 0) : 0;

  const getProgressPercentage = (topicId: string) => {
    const progress = getProgress(topicId);
    return progress?.read_pct || 0;
  };

  const getNextTopic = () => {
    return topics.find(topic => getProgressPercentage(topic.id) < 100);
  };

  const completedTopics = topics.filter(topic => getProgressPercentage(topic.id) === 100);
  const inProgressTopics = topics.filter(topic => {
    const progress = getProgressPercentage(topic.id);
    return progress > 0 && progress < 100;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">
          Volledige Training
        </h1>
        <p className="text-lg text-gray-600 max-w-3xl mx-auto">
          Een complete training door alle AI-geletterdheid onderwerpen. 
          Werk systematisch door alle content, flashcards en quizzen.
        </p>
      </div>

      {/* Progress Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="text-center">
          <div className="w-12 h-12 bg-brand-primary/10 rounded-lg flex items-center justify-center mx-auto mb-4">
            <BookOpen className="w-6 h-6 text-brand-primary" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Totaal</h3>
          <p className="text-2xl font-bold text-brand-primary">{topics.length}</p>
          <p className="text-sm text-gray-500">Onderwerpen</p>
        </Card>
        <Card className="text-center">
          <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-6 h-6 text-green-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Voltooid</h3>
          <p className="text-2xl font-bold text-green-600">{completedTopics.length}</p>
          <p className="text-sm text-gray-500">Onderwerpen</p>
        </Card>
        <Card className="text-center">
          <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mx-auto mb-4">
            <Play className="w-6 h-6 text-yellow-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Bezig</h3>
          <p className="text-2xl font-bold text-yellow-600">{inProgressTopics.length}</p>
          <p className="text-sm text-gray-500">Onderwerpen</p>
        </Card>
        <Card className="text-center">
          <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mx-auto mb-4">
            <BookOpen className="w-6 h-6 text-gray-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Nog te doen</h3>
          <p className="text-2xl font-bold text-gray-600">{topics.length - completedTopics.length - inProgressTopics.length}</p>
          <p className="text-sm text-gray-500">Onderwerpen</p>
        </Card>
      </div>

      {/* Continue or Start */}
      {getNextTopic() ? (
        <Card className="bg-blue-50 border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-blue-900 mb-1">
                {inProgressTopics.length > 0 ? 'Ga verder waar je was' : 'Start je training'}
              </h3>
              <p className="text-blue-700">
                {inProgressTopics.length > 0 
                  ? `Volgende onderwerp: ${getNextTopic()?.title}`
                  : 'Begin met het eerste onderwerp'
                }
              </p>
            </div>
            <Link to={`/topics/${getNextTopic()?.slug}`}>
              <Button className="bg-blue-600 hover:bg-blue-700">
                {inProgressTopics.length > 0 ? 'Ga Verder' : 'Start Training'}
              </Button>
            </Link>
          </div>
        </Card>
      ) : (
        <Card className="bg-green-50 border-green-200">
          <div className="text-center">
            <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-green-900 mb-2">
              Gefeliciteerd! 🎉
            </h3>
            <p className="text-green-700 mb-4">
              Je hebt alle onderwerpen voltooid. Bekijk je voortgang of herhaal specifieke onderwerpen.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/progress">
                <Button variant="outline" className="border-green-300 text-green-700 hover:bg-green-100">
                  Bekijk Voortgang
                </Button>
              </Link>
              <Link to="/topics">
                <Button className="bg-green-600 hover:bg-green-700">
                  Herhaal Onderwerpen
                </Button>
              </Link>
            </div>
          </div>
        </Card>
      )}

      {/* All Topics */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Alle Onderwerpen</h2>
        <div className="space-y-4">
          {topics.map((topic, index) => {
            const progress = getProgressPercentage(topic.id);
            const isCompleted = progress === 100;
            const isInProgress = progress > 0 && progress < 100;
            
            return (
              <Card key={topic.id} className={`transition-all ${
                isCompleted ? 'bg-green-50 border-green-200' : 
                isInProgress ? 'bg-blue-50 border-blue-200' : ''
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      isCompleted ? 'bg-green-100' : 
                      isInProgress ? 'bg-blue-100' : 'bg-gray-100'
                    }`}>
                      {isCompleted ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <span className={`text-sm font-bold ${
                          isInProgress ? 'text-blue-600' : 'text-gray-600'
                        }`}>
                          {index + 1}
                        </span>
                      )}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{topic.title}</h3>
                      {topic.summary && (
                        <p className="text-sm text-gray-500">{topic.summary}</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    <div className="text-right">
                      <div className="text-sm text-gray-500">Voortgang</div>
                      <div className="text-lg font-semibold text-gray-900">{progress}%</div>
                    </div>
                    
                    <div className="w-24 bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all duration-300 ${
                          isCompleted ? 'bg-green-500' : 
                          isInProgress ? 'bg-blue-500' : 'bg-gray-300'
                        }`}
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                    
                    <Link to={`/topics/${topic.slug}`}>
                      <Button 
                        variant={isCompleted ? "outline" : "primary"}
                        size="sm"
                      >
                        {isCompleted ? 'Bekijk' : isInProgress ? 'Ga Verder' : 'Start'}
                      </Button>
                    </Link>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Training Stats */}
      <Card className="bg-gray-50">
        <div className="text-center">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Training Statistieken</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <div className="text-2xl font-bold text-brand-primary mb-1">{totalSections}</div>
              <div className="text-sm text-gray-500">Content Secties</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-brand-primary mb-1">{totalFlashcards}</div>
              <div className="text-sm text-gray-500">Flashcards</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-brand-primary mb-1">{totalQuestions}</div>
              <div className="text-sm text-gray-500">Quiz Vragen</div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};
